/**
 * Camera bounds of a level: the CameraController of the level's Main Camera keeps the view inside
 * `cameraMaxBounds` (x, y = bottom left, width, height in tiles) when `useRectBounds` is on.
 */
import type { Draft } from 'immer';
import type { LevelDoc } from '../model/format.ts';
import { compData, objectComps, writeField, type CompAddr } from './components.ts';
import { findObject } from './store.ts';
import { bodyBounds, objectInfo } from './scene.ts';

export interface CameraBounds {
  enabled: boolean;
  x: number;
  y: number;
  w: number;
  h: number;
}

const num = (v: unknown, d = 0) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : d;
};

const addrCache = new WeakMap<LevelDoc, CompAddr | null>();

/** The level camera's CameraController (null when the level has no camera object). */
export function cameraAddr(doc: LevelDoc): CompAddr | null {
  if (addrCache.has(doc)) return addrCache.get(doc)!;
  const id = doc.system.camera;
  const f = id ? findObject(doc, id) : null;
  const c = f ? objectComps(doc, f.obj).find((x) => x.type === 'CameraController') : undefined;
  const a = c?.addr ?? null;
  addrCache.set(doc, a);
  return a;
}

export function cameraBounds(doc: LevelDoc): CameraBounds | null {
  const a = cameraAddr(doc);
  const d = a ? compData(doc, a) : null;
  if (!d) return null;
  const b = d.cameraMaxBounds ?? {};
  return { enabled: num(d.useRectBounds) !== 0, x: num(b.x), y: num(b.y), w: num(b.width), h: num(b.height) };
}

/** Tiles the camera shows at an aspect ratio (its minimum viewport, or the fixed size it is set to). */
export function cameraView(doc: LevelDoc, aspect = 16 / 9): [number, number] {
  const a = cameraAddr(doc);
  const d = a ? compData(doc, a) : null;
  const fixed = num(d?.overrideCameraSize);
  const half = fixed > 0 ? fixed : Math.max(num(d?.minViewportTiles?.y, 24) / 2, num(d?.minViewportTiles?.x, 42) / (2 * aspect));
  return [2 * half * aspect, 2 * half];
}

const fmt = (v: number) => String(Math.round(v * 1000) / 1000);

/** Write bounds fields; `a` is cameraAddr of the document being changed. */
export function writeCameraBounds(d: Draft<LevelDoc>, a: CompAddr, b: Partial<CameraBounds>) {
  if (b.enabled !== undefined) writeField(d, a, 'useRectBounds', { v: b.enabled ? '1' : '0' });
  if (b.x !== undefined) writeField(d, a, 'cameraMaxBounds.x', { v: fmt(b.x) });
  if (b.y !== undefined) writeField(d, a, 'cameraMaxBounds.y', { v: fmt(b.y) });
  if (b.w !== undefined) writeField(d, a, 'cameraMaxBounds.width', { v: fmt(b.w) });
  if (b.h !== undefined) writeField(d, a, 'cameraMaxBounds.height', { v: fmt(b.h) });
}

/** Bounds around the level's terrain, tiles, platforms and visible objects, with a margin (whole tiles). */
export function fitCameraBounds(doc: LevelDoc, margin = 2): Omit<CameraBounds, 'enabled'> | null {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const add = (x: number, y: number) => {
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  };
  for (const t of doc.terrains) if (t.mode === 'add') for (const r of t.rings) for (const [x, y] of r) add(x, y);
  for (const l of doc.layers)
    for (let i = 0; i + 3 < l.tiles.length; i += 4) {
      add(l.tiles[i], l.tiles[i + 1]);
      add(l.tiles[i] + 1, l.tiles[i + 1] + 1);
    }
  for (const body of doc.bodies) {
    const b = bodyBounds(doc, body);
    if (b) {
      add(b[0], b[1]);
      add(b[2], b[3]);
    }
  }
  const system = new Set(Object.values(doc.system));
  for (const o of doc.objects) {
    if (system.has(o.id)) continue;
    // invisible zones (death zones, triggers) often reach far past the playable area
    const info = objectInfo(doc, o);
    if (!info.drawables.length) {
      add(o.x, o.y);
      continue;
    }
    add(info.bounds[0], info.bounds[1]);
    add(info.bounds[2], info.bounds[3]);
  }
  if (!Number.isFinite(x0)) return null;
  const x = Math.floor(x0) - margin, y = Math.floor(y0) - margin;
  return { x, y, w: Math.ceil(x1) + margin - x, h: Math.ceil(y1) + margin - y };
}
